<template>
    <TransitionRoot
        as="template"
        :show="_open"
    >
        <Dialog
            :initial-focus="closeButtonRef"
            as="div"
            class="relative z-10"
            @close="close(false)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed z-10 inset-0 overflow-y-auto">
                <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <QuestionMarkCircleIcon
                                        class="h-6 w-6 text-yellow-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                    <DialogTitle
                                        as="h3"
                                        class="text-lg leading-6 font-medium text-gray-900"
                                    >
                                        {{ _title ?? "Are you sure?" }}
                                    </DialogTitle>
                                    <div class="mt-2 w-full">
                                        {{ _messageContents }}
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 text-base font-medium text-white focus:outline-hidden focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                                    :class="_yesIsBad ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 sm:ml-3' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'"
                                    @click="close(true)"
                                >
                                    {{ _yesButtonText }}
                                </button>
                                <button
                                    ref="closeButtonRef"
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 text-base font-medium text-white focus:outline-hidden focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
                                    :class="_yesIsBad ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 sm:ml-3'"
                                    @click="close(false)"
                                >
                                    {{ _noButtonText }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline';
import { Ref, ref } from 'vue';
import { TransitionRoot, TransitionChild, DialogPanel, DialogTitle, Dialog } from '@headlessui/vue';
import { ModalConfig } from '@/stores/modals';

export interface ConfirmConfig extends ModalConfig {
    yesButtonText?: string;
    noButtonText?: string;
    yesIsBad?: boolean;
}

export interface ConfirmModal {
    show: (message: string | ConfirmConfig) => Promise<boolean>;
}

const _open = ref(false);
const _close = ref(false);
const _messageContents: Ref<string | null> = ref(null);
const _title: Ref<string | null> = ref(null);
const _yesButtonText: Ref<string | null> = ref(null);
const _yesIsBad: Ref<boolean> = ref(false);
const _noButtonText: Ref<string | null> = ref(null);
const _response: Ref<boolean> = ref(false);
const closeButtonRef = ref(null);

const timeout = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

async function show(message: string | ConfirmConfig) {
    _messageContents.value = null;
    _title.value = null;
    _yesButtonText.value = null;
    _noButtonText.value = null;
    if (typeof message == "string") {
        _messageContents.value = message;
    }
    else {
        _messageContents.value = message.message
        _title.value = message.title;
        _yesButtonText.value = message.yesButtonText ?? "Yes";
        _noButtonText.value = message.noButtonText ?? "No";
        _yesIsBad.value = message.yesIsBad ?? false;
    }
    _open.value = true;
    while (_close.value === false) await timeout(50);
    _close.value = false;
    _open.value = false;

    return _response.value;
}

function close(val: boolean = false) {
    _close.value = true;
    _response.value = val;
}

defineExpose({
    show
})
</script>